import { useEffect, useState } from 'react';
import axios from 'utils/axios';
import type { WarrantyListQuery, WarrantyObjectOption } from '@econtractor/types';
import { useWarrantyRegistry } from '../context/WarrantyProvider';

export type WarrantyFilterValues = Pick<
    WarrantyListQuery,
    'categoryId' | 'vendorId' | 'objectId' | 'unassigned' | 'expired' | 'archived'
>;

const defaultFilters: WarrantyFilterValues = {
    categoryId: undefined,
    vendorId: undefined,
    objectId: undefined,
    unassigned: false,
    expired: 'all',
    archived: false,
};

const useWarrantyFilters = () => {
    const { query, setQuery } = useWarrantyRegistry();
    const [data, setData] = useState<{
        categories: { ID: number; name: string }[];
        objects: WarrantyObjectOption[];
    }>({ categories: [], objects: [] });
    const [show, setShow] = useState(false);
    const [draft, setDraft] = useState<WarrantyFilterValues>(defaultFilters);
    const active = Boolean(
        query.categoryId ||
        query.vendorId ||
        query.objectId ||
        query.unassigned ||
        query.archived ||
        (query.expired && query.expired !== 'all'),
    );

    useEffect(() => {
        let cancelled = false;

        void axios
            .get('/warranties/filters')
            .then((res) => {
                if (!cancelled) setData(res.data);
            })
            .catch(() => undefined);

        return () => {
            cancelled = true;
        };
    }, []);

    const open = () => {
        setDraft({
            categoryId: query.categoryId,
            vendorId: query.vendorId,
            objectId: query.objectId,
            unassigned: Boolean(query.unassigned),
            expired: query.expired || 'all',
            archived: Boolean(query.archived),
        });
        setShow(true);
    };

    const close = () => setShow(false);

    const apply = () => {
        setQuery((previous) => ({ ...previous, ...draft }));
        close();
    };

    const reset = () => {
        setDraft(defaultFilters);
        setQuery((previous) => ({ ...previous, ...defaultFilters }));
        close();
    };

    return { ...data, show, draft, setDraft, active, open, close, apply, reset };
};

export default useWarrantyFilters;
